// The transport response and the committed Firestore snapshot are independent
// receipts. Either can confirm delivery; a delayed response must not undo it.
const definiteFailures = new Set([
  'unauthenticated', 'permission-denied', 'not-found', 'invalid-argument',
  'failed-precondition', 'already-exists',
]);

const labels = {
  sending: 'Invio…',
  waiting: 'Conferma in attesa. Non inviare un secondo messaggio.',
  error: 'Messaggio non inviato.',
  sent: 'Inviato',
};

export function createChatDelivery({
  send, createId, changed = () => {}, onQueued = () => {},
  onConfirmed = () => {}, delayMs = 12000,
  setTimer = setTimeout, clearTimer = clearTimeout,
}) {
  const entries = new Map();
  const attempts = new Set();
  let generation = 0;
  let disposed = false;

  const matches = (entry, message) => Boolean(entry && message && message.id === entry.id &&
    typeof message.content === 'string' && message.content.trim() === entry.content);
  const isCurrent = (entry, epoch) => !disposed && generation === epoch && entries.get(entry.id) === entry;
  const expose = (entry) => ({
    ...entry,
    deliveryState: entry.state,
    deliveryLabel: entry.error || labels[entry.state],
    canRetry: ['waiting', 'error'].includes(entry.state),
  });
  const publish = () => changed(Array.from(entries.values(), expose));
  const settle = (attempt, confirmed = false) => {
    if (!attempts.delete(attempt)) return;
    clearTimer(attempt.timer);
    attempt.resolve(confirmed);
  };
  const confirm = (entry, message, epoch) => {
    if (!isCurrent(entry, epoch) || !matches(entry, message)) return false;
    const firstConfirmation = entry.state !== 'sent';
    entry.message = { ...message };
    entry.state = 'sent';
    entry.error = '';
    for (const attempt of attempts) {
      if (attempt.entry === entry) settle(attempt, true);
    }
    publish();
    if (firstConfirmation) onConfirmed(expose(entry));
    return true;
  };

  const start = (entry) => {
    if (disposed || entry.state === 'sent' || entry.state === 'sending') return Promise.resolve(false);
    const epoch = generation;
    entry.state = 'sending';
    entry.error = '';
    entry.attempt = (entry.attempt || 0) + 1;
    const number = entry.attempt;
    publish();
    return new Promise((resolve) => {
      const attempt = { entry, resolve, timer: null };
      attempts.add(attempt);
      attempt.timer = setTimer(() => {
        if (isCurrent(entry, epoch) && entry.state !== 'sent' && entry.attempt === number) {
          entry.state = 'waiting';
          publish();
        }
        settle(attempt);
      }, delayMs);
      // Keep handling the original request after the visible wait ends. Retrying
      // uses the SAME ID, so even two committed requests produce one message.
      let request;
      try {
        request = send({ ticketId: entry.ticketId, content: entry.content, messageId: entry.id });
      } catch (error) {
        request = Promise.reject(error);
      }
      Promise.resolve(request).then((result) => {
        if (confirm(entry, result?.message, epoch)) return;
        if (isCurrent(entry, epoch) && entry.state !== 'sent' && entry.attempt === number) {
          entry.state = 'waiting';
          publish();
        }
        settle(attempt);
      }, (error) => {
        if (isCurrent(entry, epoch) && entry.state !== 'sent' && entry.attempt === number) {
          const code = String(error?.code || '').replace(/^(functions|firestore)\//, '');
          entry.state = definiteFailures.has(code) ? 'error' : 'waiting';
          entry.error = code === 'unauthenticated'
            ? 'Accedi di nuovo per inviare questo messaggio.'
            : code === 'permission-denied' ? 'Non puoi inviare messaggi in questa conversazione.'
              : code === 'failed-precondition' ? 'La conversazione è completata. Apri un nuovo ticket.'
                : '';
          publish();
        }
        settle(attempt, entry.state === 'sent');
      });
    });
  };

  const isSending = (ticketId) => Array.from(entries.values()).some(
    entry => entry.ticketId === ticketId && entry.state === 'sending',
  );
  const reset = () => {
    generation += 1;
    for (const attempt of attempts) settle(attempt);
    entries.clear();
    publish();
  };

  return {
    snapshot: () => Array.from(entries.values(), expose),
    isSending,
    status(ticketId) {
      const current = Array.from(entries.values()).filter(entry => entry.ticketId === ticketId);
      const unresolved = current.find(entry => entry.state !== 'sent');
      return unresolved ? expose(unresolved).deliveryLabel : current.length ? 'Messaggio inviato' : '';
    },
    submit(ticketId, rawContent) {
      const content = typeof rawContent === 'string' ? rawContent.trim() : '';
      if (disposed || !ticketId || !content || content.length > 20000 || isSending(ticketId)) {
        return Promise.resolve(false);
      }
      let entry = Array.from(entries.values()).find(item => item.ticketId === ticketId &&
        item.content === content && item.state !== 'sent');
      if (!entry) {
        const id = createId();
        entry = { id, ticketId, content, state: 'queued', error: '',
          message: { id, content, type: 'Richiesta', timestamp: new Date().toISOString() } };
        entries.set(id, entry);
      }
      onQueued(expose(entry));
      return start(entry);
    },
    retry(ticketId, id) {
      const entry = entries.get(id);
      if (!entry || entry.ticketId !== ticketId || isSending(ticketId)) return Promise.resolve(false);
      return start(entry);
    },
    reconcile(messages, metadata = {}, ticketId) {
      if (metadata?.hasPendingWrites || metadata?.fromCache || disposed) return;
      const byId = new Map((Array.isArray(messages) ? messages : []).map(message => [message.id, message]));
      let removed = false;
      for (const entry of entries.values()) {
        if (entry.ticketId !== ticketId) continue;
        const message = byId.get(entry.id);
        if (matches(entry, message)) {
          entry.canonicalSeen = true;
          confirm(entry, message, generation);
        } else if (entry.canonicalSeen && entry.state === 'sent') {
          // A later authoritative removal (for example by staff) must not be
          // undone by the optimistic overlay. Initial stale snapshots are safe.
          entries.delete(entry.id);
          removed = true;
        }
      }
      const confirmed = Array.from(entries.values()).filter(entry => entry.canonicalSeen && entry.state === 'sent');
      for (const entry of confirmed.slice(0, Math.max(0, confirmed.length - 50))) {
        entries.delete(entry.id);
        removed = true;
      }
      if (removed) publish();
    },
    visibleConversations(messages = [], ticketId) {
      const canonical = Array.isArray(messages) ? messages : [];
      const visible = canonical.map((message) => {
        const entry = entries.get(message.id);
        // While leaving a route, both a missing entry and route ticket ID can
        // be undefined. Equality alone must not count as an outbox match.
        return entry && entry.ticketId === ticketId && matches(entry, message)
          ? { ...message, ...pickDelivery(expose(entry)) } : message;
      });
      for (const entry of entries.values()) {
        if (entry.ticketId === ticketId && !canonical.some(message => matches(entry, message))) {
          visible.push({ ...entry.message, ...pickDelivery(expose(entry)) });
        }
      }
      return visible;
    },
    reset,
    dispose() {
      disposed = true;
      reset();
    },
  };
}

function pickDelivery(entry) {
  return { deliveryState: entry.deliveryState, deliveryLabel: entry.deliveryLabel, canRetry: entry.canRetry };
}
